import { Box, Divider, useTheme } from "@mui/material";
import { useSearchParams } from "react-router-dom";

import {
  PageLayout,
  RobotoStyledTab,
  RobotoStyledTabs,
  RobotoTabPanel,
} from "@/shared/components";

import { SessionsTab } from "./Sessions/SessionsTab";

const ACTIVE_PANEL_FLEX_SX = {
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
} as const;

const INNER_BOX_FLEX_SX = {
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
} as const;

/**
 * Top-level Agents page.
 *
 * Renders a single "Sessions" tab today so the shell is in place for the
 * follow-up Agents (templates) tab — at that point the second
 * ``RobotoStyledTab`` lands here without touching the routing or layout.
 *
 * Layout note: the page height flows through a flex chain (page-content →
 * outer Box → tab body Box → active panel → inner Box → tab body) so the
 * inner ``DataTable`` can take the remaining vertical space, matching the
 * Datasets / Events tables. The ``display: flex`` sx is applied to the
 * panel's outer Box **only when active** — applying it unconditionally
 * would override the ``hidden`` attribute on the inactive panel and leave
 * both panels taking flex space at once.
 */
export function AgentsTabs() {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabSearchParam = searchParams.get("tab");
  const tabValue = tabSearchParam !== null ? parseInt(tabSearchParam) : 0;

  return (
    <PageLayout title={"Roboto - Agents"} headerContentLeft={<>Agents</>}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
        }}
      >
        <div>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <RobotoStyledTabs
              value={tabValue}
              onChange={(_event, newValue) => {
                setSearchParams({ tab: String(newValue) });
              }}
              aria-label="agents tabs"
            >
              <RobotoStyledTab label="Sessions" />
            </RobotoStyledTabs>
          </Box>
          <Divider sx={{ borderColor: theme.border.color }} />
        </div>

        <Box
          sx={{
            // ``flex: 1`` + ``minHeight: 0`` together let the tab body fill
            // the remaining page-content height while still letting an
            // inner ``DataTable`` scroll its own overflow. Without
            // ``minHeight: 0`` the flex item refuses to shrink below its
            // content size and the inner ``height: 100%`` chain collapses.
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            padding: theme.spacing(2, 0),
            paddingBottom: theme.spacing(1),
          }}
        >
          <RobotoTabPanel
            index={0}
            value={tabValue}
            sx={{
              ...(tabValue === 0 ? ACTIVE_PANEL_FLEX_SX : {}),
              paddingTop: theme.spacing(1),
              paddingBottom: theme.spacing(1),
            }}
            innerBoxSx={INNER_BOX_FLEX_SX}
          >
            <SessionsTab />
          </RobotoTabPanel>
        </Box>
      </Box>
    </PageLayout>
  );
}
